import { ArcElement, Chart as ChartJS, Legend, SubTitle, Title, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment/min/moment-with-locales";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Modal, Row, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { FiletypePng } from "react-bootstrap-icons";
import { Chart } from "react-chartjs-2";
import { useRouteLoaderData } from "react-router-dom";
import ErrorResponse from "./ErrorResponse";
import { formatDemographicText, toAutoDecimals, formatDaypartText } from "./HelperFunctions";
import { useFetchData, useRadioName } from "./HelperHooks";
import LoadingIndicator from "./LoadingIndicator";
import { backgroundPlugin } from "./chartOptions";

ChartJS.register(ArcElement, Legend, Tooltip, ChartDataLabels, Title, SubTitle);

const EMPTY_ARRAY = [];

function formatDaysText(days) {
    if (!days?.length) return null;
    const startDay = moment().weekday(days[0]).format("ddd");
    const endDay = moment()
        .weekday(days[days.length - 1])
        .format("ddd");

    if (days.length === 1) return startDay;
    return `${startDay}-${endDay}`;
}

function formatTimeWindowText(startTime, endTime) {
    if (!startTime || !endTime) return null;
    return `${moment(startTime, "HH:mm:ss").format("LT")} - ${moment(endTime, "HH:mm:ss").format("LT")}`;
}

function formatMinuteRangeText(startMinute, endMinute) {
    if (startMinute === null || startMinute === undefined || endMinute === null || endMinute === undefined) return null;
    if (startMinute === 0 && endMinute >= 59) return null;
    return `Minutes ${startMinute}-${endMinute}`;
}

function formatDurationText(minDuration, maxDuration) {
    if (!(minDuration > 0 || maxDuration > 0)) return null;

    const formatDuration = totalSeconds => {
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
        const seconds = String(totalSeconds % 60).padStart(2, "0");
        return `${minutes}:${seconds}`;
    };

    return `Duration ${formatDuration(minDuration)} - ${maxDuration ? formatDuration(maxDuration) : "∞"}`;
}

export default function ContentGroupTrend({
    show,
    onHide,
    radioId,
    contentGroupId,
    keywords = "",
    searchField = "keywords",
    dataType,
    demoId = 1,
    monthsLimit = 12,
    contentGroups,
    selectedContentGroupIds = EMPTY_ARRAY,
    filtered = false,
    itemTypes = [],
    includeHidden = false,
    includeInvalid = false,
    daypart = null,
    days = EMPTY_ARRAY,
    start_time = null,
    end_time = null,
    start_minute = null,
    end_minute = null,
    min_duration = null,
    max_duration = null,
    start_datetime,
    end_datetime,
}) {
    const { radios, demographics } = useRouteLoaderData("root");
    const [loading, errorCode, data, fetchData] = useFetchData();
    const radioName = useRadioName();
    const [currentRadioName, setCurrentRadioName] = useState("");
    const [grouping, setGrouping] = useState("week");
    const [demoName, setDemoName] = useState("");
    const [daypartName, setDaypartName] = useState("");
    const chartRef = useRef(null);
    const [timeRange, setTimeRange] = useState("3months");
    const [percentageGrouping, setPecentageGrouping] = useState("breaks");

    useEffect(() => {
        const fetchRadioName = async () => {
            try {
                const radioName = (await radios).data.find(x => x.radio_id === radioId)?.radio_name;
                if (radioName) {
                    setCurrentRadioName(radioName);
                }
            } catch (error) {
                console.error("Error fetching radio name:", error);
            }
        };

        fetchRadioName();
    }, [radioId, radios]);

    useEffect(() => {
        setDaypartName(formatDaypartText(daypart) ?? "");
    }, [dataType, daypart, radioId]);

    useEffect(() => {
        async function loadData() {
            const input = {
                radio_id: radioId,
                data_type: dataType,
                demo_id: demoId,
            };

            if (contentGroupId) {
                input.content_groups = [contentGroupId];
                input.daypart_id = daypart?.daypart_id ?? null;
                input.days = days;
                input.start_time = start_time;
                input.end_time = end_time;
                input.start_minute = start_minute;
                input.end_minute = end_minute;
                input.min_duration = min_duration;
                input.max_duration = max_duration;
                await fetchData("content_testing/trend", input);
            } else if (filtered) {
                input[searchField === "transcription" ? "transcription" : "keywords"] = keywords;
                input.content_groups = selectedContentGroupIds;
                input.include_hidden = includeHidden;
                input.exclude_invalid = includeInvalid;
                input.daypart_id = daypart?.daypart_id ?? null;
                input.days = days;
                input.start_time = start_time;
                input.end_time = end_time;
                input.start_minute = start_minute;
                input.end_minute = end_minute;
                input.min_duration = min_duration;
                input.max_duration = max_duration;

                if (timeRange === "3months") {
                    input.months_limit = 3;
                }
                if (timeRange === "1year") {
                    input.months_limit = monthsLimit;
                }
                if (timeRange === "timeframe") {
                    input.start_datetime = start_datetime;
                    input.end_datetime = end_datetime;
                }

                await fetchData("content_testing/trend", input);
            }
        }
        if (filtered) {
            loadData();
        } else {
            if (radioId && (contentGroupId || (keywords && keywords.trim().length > 0))) {
                loadData();
            }
        }
    }, [
        contentGroupId,
        keywords,
        searchField,
        dataType,
        daypart,
        days,
        demoId,
        end_datetime,
        end_minute,
        end_time,
        fetchData,
        includeHidden,
        includeInvalid,
        max_duration,
        min_duration,
        monthsLimit,
        radioId,
        selectedContentGroupIds,
        start_datetime,
        start_minute,
        start_time,
        timeRange,
    ]);

    useEffect(() => {
        async function getDemoName() {
            const demo = (await demographics).data.find(x => x.demo_id === demoId);
            setDemoName(formatDemographicText(demo));
        }
        if (demoId) getDemoName();
    }, [demoId, demographics]);

    const chartTitle = useMemo(() => {
        if (contentGroupId) {
            return contentGroups.find(x => x.group_id === contentGroupId)?.display_name;
        } else if (filtered) {
            return selectedContentGroupIds.length || keywords
                ? `Filtered trend for ${keywords ? `${searchField === "transcription" ? "transcription" : "keywords"} '${keywords}'` : ""}${
                      keywords && selectedContentGroupIds.length ? " in " : ""
                  }${selectedContentGroupIds
                      .map(id => contentGroups.find(x => x.group_id === id)?.display_name)
                      .filter(Boolean)
                      .join(", ")}`
                : "Filtered trend";
        }
        return "Trend Data";
    }, [contentGroupId, contentGroups, filtered, keywords, searchField, selectedContentGroupIds]);

    const hasScores = useMemo(() => data?.data_week?.filter(x => x.score > 0)?.length, [data?.data_week]);

    const percentageKey = percentageGrouping === "breaks" ? "percentage_breaks" : "percentage_duration";

    const subtitleText = useMemo(() => {
        const subtitleParts = [currentRadioName];

        if (filtered) {
            if (daypartName) {
                subtitleParts.push(daypartName);
            } else {
                const dayAndTime = [formatDaysText(days), formatTimeWindowText(start_time, end_time)].filter(Boolean).join(" ");
                if (dayAndTime) subtitleParts.push(dayAndTime);
                const minuteRangeText = formatMinuteRangeText(start_minute, end_minute);
                if (minuteRangeText) subtitleParts.push(minuteRangeText);
                const durationText = formatDurationText(min_duration, max_duration);
                if (durationText) subtitleParts.push(durationText);
            }
        } else if (daypartName) {
            subtitleParts.push(daypartName);
        } else {
            const dayAndTime = [formatDaysText(days), formatTimeWindowText(start_time, end_time)].filter(Boolean).join(" ");
            if (dayAndTime) subtitleParts.push(dayAndTime);
            const minuteRangeText = formatMinuteRangeText(start_minute, end_minute);
            if (minuteRangeText) subtitleParts.push(minuteRangeText);
            const durationText = formatDurationText(min_duration, max_duration);
            if (durationText) subtitleParts.push(durationText);
        }

        if (demoName) subtitleParts.push(demoName);

        if (filtered) {
            if (timeRange === "3months") {
                subtitleParts.push("Latest 3 months");
            } else if (timeRange === "1year") {
                subtitleParts.push(monthsLimit ? `Latest ${monthsLimit} months` : "All available data");
            } else if (timeRange === "timeframe" && start_datetime && end_datetime) {
                subtitleParts.push(`${moment(start_datetime).format("L")} - ${moment(end_datetime).format("L")}`);
            }
        } else if (monthsLimit) {
            subtitleParts.push(`Latest ${monthsLimit} months`);
        }

        return subtitleParts.filter(Boolean).join(" | ");
    }, [
        currentRadioName,
        daypartName,
        days,
        demoName,
        end_datetime,
        end_minute,
        end_time,
        filtered,
        max_duration,
        min_duration,
        monthsLimit,
        start_datetime,
        start_minute,
        start_time,
        timeRange,
    ]);

    const chartOptions = {
        aspectRatio: window.innerHeight - 100 < window.innerWidth || window.innerWidth > 800 ? 2 : 1,
        spanGaps: true,
        plugins: {
            title: {
                display: true,
                text: chartTitle,
                font: { size: 18 },
                padding: { bottom: 5 },
            },
            subtitle: {
                display: true,
                text: subtitleText,
            },
            datalabels: {
                color: "#142533",
                font: { weight: "bold" },
            },
            legend: {
                position: "bottom",
                labels: { usePointStyle: true },
                reverse: true,
            },
            customCanvasBackgroundColor: { color: "white" },
            tooltip: { enabled: false },
        },
        scales: {
            percentage: {
                position: "right",
                type: "linear",
                // ticks: {
                //     callback: function (value) {
                //         const dur = moment.duration(value || 0, "seconds");
                //         const hours = Math.floor(dur.asHours());
                //         const mins = Math.floor(dur.minutes());
                //         if (hours > 0) return `${hours}h:${String(mins).padStart(2, "0")}m`;
                //         else return `${mins} mins`;
                //     },
                // },
                beginAtZero: true,
                grid: { display: false },
                border: { display: false },
            },
            scores: {
                type: "linear",
                max: 10,
                position: "left",
                display: hasScores,
                beginAtZero: true,
                grid: { display: false },
                border: { display: false },
                offset: true,
            },
            x: {
                grid: { display: false },
                ticks: { maxRotation: 90 },
            },
        },
        interaction: { mode: "index" },
    };

    const chartData = useMemo(() => {
        if (data) {
            const selectedGroupingData = data[`data_${grouping}`].filter(x => x[percentageKey] > 0);
            const scoreDataset = {
                type: "line",
                label: "Score",
                borderColor: "#7BA7CC",
                pointBackgroundColor: "#7BA7CC",
                borderWidth: 4,
                pointBorderWidth: 1,
                pointRadius: 6,
                pointHoverRadius: 6,
                data: selectedGroupingData.map(x => toAutoDecimals(x.score, 1)),
                yAxisID: "scores",
                datalabels: {
                    labels: {
                        index: {
                            align: "end",
                            anchor: "end",
                        },
                    },
                },
            };
            const percentageDataset = {
                type: "bar",
                backgroundColor: "#fcd76a",
                label: percentageGrouping === "breaks" ? "Breaks" : " Duration",
                yAxisID: "percentage",
                borderRadius: 5,
                pointStyle: "rectRounded",
                data: selectedGroupingData.map(x => x[percentageKey]),
                datalabels: {
                    formatter: function (value) {
                        return value ? `${Math.round(value)}%` : `${toAutoDecimals(value, 1, "")}`;
                    },
                    labels: {
                        index: {
                            align: "end",
                            anchor: "start",
                        },
                    },
                },
            };

            return {
                labels: selectedGroupingData.map(x => moment(x.start_datetime).format("L")),
                datasets: hasScores ? [scoreDataset, percentageDataset] : [percentageDataset],
            };
        }
    }, [data, grouping, hasScores, percentageGrouping]);

    async function exportPng() {
        const base64Image = chartRef.current.toBase64Image();
        const link = document.createElement("a");
        if (filtered) {
            link.download = `Filtered_Trend_${currentRadioName}.png`.replaceAll(" ", "_");
        } else {
            link.download = `Trend_${contentGroups.find(x => x.group_id === contentGroupId)?.display_name}_${currentRadioName}.png`.replaceAll(" ", "_");
        }
        link.href = base64Image;
        link.click();
    }

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="trend-over-time-modal">
            <Modal.Header closeButton className=" py-1 py-xl-3">
                <Modal.Title className=" text-uppercase">Content Group Trend</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column pt-1 row-gap-2">
                <Stack direction="horizontal" gap={2} className=" flex-wrap">
                    <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5">Group By:</span>
                    <ToggleButtonGroup type="radio" name="tot-grouping" className="ra-switcher" onChange={val => setGrouping(val)} value={grouping}>
                        <ToggleButton id={`trend-day`} value={"day"} variant="ra-dark-blue-outline" className=" align-content-center">
                            Day
                        </ToggleButton>
                        <ToggleButton id={`trend-week`} value={"week"} variant="ra-dark-blue-outline" className=" align-content-center">
                            Week
                        </ToggleButton>
                        <ToggleButton id={`trend-month`} value={"month"} variant="ra-dark-blue-outline" className="align-items-center column-gap-1">
                            Month
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {filtered ? (
                        <>
                            <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5">Limit:</span>
                            <ToggleButtonGroup
                                type="radio"
                                name="time-range-picker"
                                value={timeRange}
                                onChange={val => setTimeRange(val)}
                                className="ra-switcher">
                                <ToggleButton id="time-3months" value="3months" variant="ra-dark-blue-outline" className="align-content-center">
                                    3 Months
                                </ToggleButton>
                                <ToggleButton id="time-1year" value="1year" variant="ra-dark-blue-outline" className="align-content-center text-nowrap">
                                    1 Year
                                </ToggleButton>
                                <ToggleButton id="timeframe" value="timeframe" variant="ra-dark-blue-outline" className="align-content-center text-nowrap">
                                    Timeframe
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </>
                    ) : null}

                    <Col xs={12} md="auto">
                        <ToggleButtonGroup
                            type="radio"
                            name="tot-grouping-percentage"
                            className="ra-switcher w-100"
                            onChange={val => setPecentageGrouping(val)}
                            value={percentageGrouping}>
                            <ToggleButton id="percentage-breaks-tot" value="breaks" variant="ra-dark-blue-outline" className="px-1">
                                % Breaks
                            </ToggleButton>
                            <ToggleButton id="percentage-duration-tot" value="duration" variant="ra-dark-blue-outline" className="px-1">
                                % Duration
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                    <Button
                        variant={"contained"}
                        onClick={exportPng}
                        disabled={!data}
                        className="p-0 border-0 btn-download ms-auto d-flex align-items-center column-gap-2">
                        <FiletypePng width={35} height={35} />
                        <span className="d-none d-md-inline-block">Screenshot</span>
                    </Button>
                </Stack>

                <div>
                    {loading && <LoadingIndicator width={75} />}
                    {!loading && errorCode && <ErrorResponse errorCode={errorCode} />}
                    {data && <Chart ref={chartRef} data={chartData} options={chartOptions} plugins={[backgroundPlugin]} />}
                </div>
            </Modal.Body>
        </Modal>
    );
}
